import { useEffect, useRef } from 'react';
import { Checkout } from '../types/checkout';
import {
  loadContentsquare,
  callContentsquare,
  isContentsquareAvailable,
  setContentsquareProperty,
  trackContentsquareEvent,
} from '../utils/load-contentsquare';
import {
  ANALYTICS_EVENTS,
  CheckoutStep,
  PaymentMethod,
  ValidationField,
  PaymentStatus,
} from '../utils/analytics-constants';
import { TemplateCheckoutEnum } from '../types/enums/template-checkout';

interface ContentsquareContext {
  merchantId?: string;
  productId?: string;
  checkoutId?: string;
  template?: TemplateCheckoutEnum | string;
  country?: string;
  language?: string;
  isAffiliate?: boolean;
}

export const useContentsquare = (checkout: Checkout | null | undefined) => {
  const taggedRef = useRef(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!checkout?.id || loadedRef.current) return;

    const initializeContentsquare = async () => {
      try {
        await loadContentsquare();
        loadedRef.current = true;

        if (isContentsquareAvailable() && checkout) {
          tagSession(checkout);
          emitCheckoutLoaded(checkout);
        }
      } catch (error) {
        console.error('[Contentsquare] Erro durante inicialização:', error);
      }
    };

    initializeContentsquare();
  }, [checkout?.id]);

  const tagSession = (checkoutData: Checkout) => {
    if (!isContentsquareAvailable() || taggedRef.current) return;

    const merchantId = checkoutData.produto?.produtorId;
    const productId = checkoutData.produtoId;
    const checkoutId = checkoutData.id;
    const template = checkoutData.template;
    const country = checkoutData.pais;
    const language = checkoutData.linguagem;
    const isAffiliate = !!checkoutData.afiliadoId;

    if (merchantId) {
      setContentsquareProperty('merchant', merchantId);
    }
    if (productId) {
      setContentsquareProperty('product', productId);
    }
    if (checkoutId) {
      setContentsquareProperty('checkout', checkoutId);
    }
    if (template) {
      setContentsquareProperty('template', String(template));
    }
    if (country) {
      setContentsquareProperty('country', country);
    }
    if (language) {
      setContentsquareProperty('language', language);
    }
    setContentsquareProperty('affiliate', isAffiliate ? 'yes' : 'no');

    taggedRef.current = true;
  };

  const emitCheckoutLoaded = (checkoutData: Checkout) => {
    const context: ContentsquareContext = {
      merchantId: checkoutData.produto?.produtorId,
      productId: checkoutData.produtoId,
      checkoutId: checkoutData.id,
      template: checkoutData.template,
      country: checkoutData.pais,
      language: checkoutData.linguagem,
      isAffiliate: !!checkoutData.afiliadoId,
    };

    trackContentsquareEvent(ANALYTICS_EVENTS.CHECKOUT_LOADED, context);
  };

  const emitStepChange = (step: CheckoutStep) => {
    trackContentsquareEvent(ANALYTICS_EVENTS.CHECKOUT_STEP_CHANGE, { step });
  };

  const emitPaymentMethodSelected = (method: PaymentMethod) => {
    trackContentsquareEvent(ANALYTICS_EVENTS.CHECKOUT_PAYMENT_METHOD_SELECTED, {
      method,
    });
  };

  const emitValidationError = (field: ValidationField) => {
    trackContentsquareEvent(ANALYTICS_EVENTS.CHECKOUT_VALIDATION_ERROR, {
      field,
    });
  };

  const emitCheckoutCompleted = (paymentStatus: PaymentStatus) => {
    trackContentsquareEvent(ANALYTICS_EVENTS.CHECKOUT_COMPLETED, {
      paymentStatus,
    });
  };

  return {
    emitStepChange,
    emitPaymentMethodSelected,
    emitValidationError,
    emitCheckoutCompleted,
    isAvailable: isContentsquareAvailable(),
  };
};
